import { LineChart, lineElementClasses } from "@mui/x-charts";
import GaugeChart from "react-gauge-chart";
import React, { useState, useEffect } from "react";
import { SensorDataProps } from "../../controller/tankController";
import "./styles.css";

declare module "react-gauge-chart";

interface ChartProps {
  data?: number[];
  gaugeValue?: number;
  showLargeGraph?: boolean;
  closeLargeGraph?: () => void;
  setShowLargeGraph: (value: boolean) => void;
  chartTitle?: string;
  sensorData?: SensorDataProps[];
  dataType?: "tankLevel" | "tankTemperature" | "batteryLevel";
  timeRange?: "7days" | "24hours";
  onTimeRangeChange?: (range: "7days" | "24hours") => void;
}

function Chart({
  setShowLargeGraph,
  sensorData = [],
  dataType = "tankLevel",
  timeRange = "7days",
  onTimeRangeChange,
  ...props
}: ChartProps) {
  const [processedData, setProcessedData] = useState<number[]>([]);
  const [xLabels, setXLabels] = useState<string[]>([]);
  const [actualDates, setActualDates] = useState<Date[]>([]);
  const [gaugeValue, setGaugeValue] = useState(0);
  const [latestVolume, setLatestVolume] = useState<string>("0.0");

  const TANK_HEIGHT = 110.3;
  const RADIUS = 38.3;

  const calculateVolume = (levelPercentage: number): number => {
    const currentHeight = (levelPercentage / 100) * TANK_HEIGHT;
    return parseFloat(
      ((Math.PI * Math.pow(RADIUS, 2) * currentHeight) / 1000).toFixed(1)
    );
  };

  const process7DayData = (sortedData: SensorDataProps[]) => {
    const now = new Date();
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(now.getDate() - 7);

    const last7DaysData = sortedData.filter(
      (item) => new Date(item.createdAt!) >= sevenDaysAgo
    );

    const dailyData: Record<string, SensorDataProps> = {};
    last7DaysData.forEach((item) => {
      const date = new Date(item.createdAt!).toLocaleDateString();
      dailyData[date] = item;
    });

    const result: number[] = [];
    const labels: string[] = [];
    const dates: Date[] = [];
    const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

    for (let i = 6; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dateString = date.toLocaleDateString();

      labels.push(days[date.getDay()]);
      dates.push(new Date(date));

      if (dailyData[dateString]) {
        let value: number;
        switch (dataType) {
          case "tankLevel":
            value = Number(dailyData[dateString].tankLevel);
            break;
          case "tankTemperature":
            value = Number(dailyData[dateString].tankTemperaure);
            break;
          case "batteryLevel":
            value = Number(dailyData[dateString].batteryLevel);
            break;
          default:
            value = Number(dailyData[dateString].tankLevel);
        }
        result.push(isNaN(value) ? 0 : value);
      } else {
        result.push(0);
      }
    }

    return { data: result, labels, dates };
  };

  const process24HourData = (sortedData: SensorDataProps[]) => {
    const now = new Date();
    const twentyFourHoursAgo = new Date();
    twentyFourHoursAgo.setHours(now.getHours() - 24);

    const last24HoursData = sortedData.filter(
      (item) => new Date(item.createdAt!) >= twentyFourHoursAgo
    );

    const hourlyData: Record<string, SensorDataProps> = {};

    last24HoursData.forEach((item) => {
      const date = new Date(item.createdAt!);
      const hourKey = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}-${date.getHours()}`;

      if (
        !hourlyData[hourKey] ||
        new Date(item.createdAt!) > new Date(hourlyData[hourKey].createdAt!)
      ) {
        hourlyData[hourKey] = item;
      }
    });

    const result: number[] = [];
    const labels: string[] = [];
    const dates: Date[] = [];

    for (let i = 23; i >= 0; i--) {
      const date = new Date();
      date.setHours(date.getHours() - i);
      const hourKey = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}-${date.getHours()}`;

      const hourLabel = date.getHours().toString().padStart(2, "0") + ":00";
      labels.push(hourLabel);
      dates.push(new Date(date));

      if (hourlyData[hourKey]) {
        let value: number;
        switch (dataType) {
          case "tankLevel":
            value = Number(hourlyData[hourKey].tankLevel);
            break;
          case "tankTemperature":
            value = Number(hourlyData[hourKey].tankTemperaure);
            break;
          case "batteryLevel":
            value = Number(hourlyData[hourKey].batteryLevel);
            break;
          default:
            value = Number(hourlyData[hourKey].tankLevel);
        }
        result.push(isNaN(value) ? 0 : value);
      } else {
        result.push(0);
      }
    }

    return { data: result, labels, dates };
  };

  useEffect(() => {
    if (sensorData.length > 0) {
      const sortedData = [...sensorData].sort(
        (a, b) =>
          new Date(a.createdAt!).getTime() - new Date(b.createdAt!).getTime()
      );

      let processedResult;

      if (timeRange === "24hours") {
        processedResult = process24HourData(sortedData);
      } else {
        processedResult = process7DayData(sortedData);
      }

      setProcessedData(processedResult.data);
      setXLabels(processedResult.labels);
      setActualDates(processedResult.dates);

      if (sortedData.length > 0) {
        const latestData = sortedData[sortedData.length - 1];
        let latestValue: number;

        switch (dataType) {
          case "tankLevel":
            latestValue = Number(latestData.tankLevel);
            setLatestVolume(calculateVolume(latestValue).toFixed(1));
            setGaugeValue(Math.min(Math.max(latestValue / 100, 0), 1));
            break;
          case "tankTemperature":
            latestValue = Number(latestData.tankTemperaure);
            setGaugeValue(latestValue);
            break;
          case "batteryLevel":
            latestValue = Number(latestData.batteryLevel);
            setGaugeValue(Math.min(Math.max(latestValue / 100, 0), 1));
            break;
          default:
            latestValue = Number(latestData.tankLevel);
            setLatestVolume(calculateVolume(latestValue).toFixed(1));
            setGaugeValue(Math.min(Math.max(latestValue / 100, 0), 1));
        }
      }
    }
  }, [sensorData, dataType, timeRange]);

  const uData =
    props.data ||
    (processedData.length > 0
      ? processedData
      : Array(timeRange === "24hours" ? 24 : 7).fill(0));
  const chartLabels =
    xLabels.length > 0
      ? xLabels
      : timeRange === "24hours"
      ? Array.from(
          { length: 24 },
          (_, i) => `${i.toString().padStart(2, "0")}:00`
        )
      : ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  const margin = { top: 10, right: 10, bottom: 10, left: 10 };
  const largeMargin = { top: 20, right: 30, bottom: 30, left: 40 };

  const getChartTitle = () => {
    if (props.chartTitle) return props.chartTitle;

    const timeRangeText = timeRange === "24hours" ? "24 Hours" : "7 Days";
    switch (dataType) {
      case "tankLevel":
        return `Tank Level History - Last ${timeRangeText} (%)`;
      case "tankTemperature":
        return `Tank Temperature History - Last ${timeRangeText} (°C)`;
      case "batteryLevel":
        return `Battery Level History - Last ${timeRangeText} (%)`;
      default:
        return `Data History - Last ${timeRangeText}`;
    }
  };

  const getYAxisDomain = () => {
    switch (dataType) {
      case "tankLevel":
        return [0, 100];
      case "tankTemperature":
        return [0, 50];
      case "batteryLevel":
        return [0, 100];
      default:
        return [0, 100];
    }
  };

  const getGaugeValue = () => {
    if (props.gaugeValue !== undefined) {
      return props.gaugeValue;
    }

    if (dataType === "tankTemperature") {
      const maxTemp = 80;
      return Math.min(Math.max(gaugeValue / maxTemp, 0), 1);
    }

    return gaugeValue;
  };

  const getGaugeFormatter = () => {
    if (dataType === "tankTemperature") {
      return (value: string) => `${Math.round(parseFloat(value) * 0.8)}°C`;
    }

    return (value: string) => `${Math.round(parseFloat(value) * 1)}%`;
  };

  const formatDateForTooltip = (date: Date) => {
    const options: Intl.DateTimeFormatOptions = {
      day: "numeric",
      month: "long",
      year: "numeric",
    };
    return date.toLocaleDateString("en-US", options);
  };

  const formatDateTimeForTooltip = (date: Date) => {
    const dateOptions: Intl.DateTimeFormatOptions = {
      day: "numeric",
      month: "long",
      year: "numeric",
    };
    const timeOptions: Intl.DateTimeFormatOptions = {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    };
    const dateStr = date.toLocaleDateString("en-US", dateOptions);
    const timeStr = date.toLocaleTimeString("en-US", timeOptions);
    return `${dateStr} at ${timeStr}`;
  };

  return (
    <div className="chart-container">
      {onTimeRangeChange && (
        <div className="time-range-selector" style={{ marginBottom: "10px" }}>
          <button
            className={`time-range-button ${
              timeRange === "7days" ? "active" : ""
            }`}
            onClick={() => onTimeRangeChange("7days")}
            style={{
              padding: "5px 10px",
              marginRight: "5px",
              backgroundColor: timeRange === "7days" ? "#4285F4" : "#f0f0f0",
              color: timeRange === "7days" ? "white" : "black",
              border: "1px solid #ccc",
              borderRadius: "4px",
              cursor: "pointer",
            }}
          >
            7 Days
          </button>
          <button
            className={`time-range-button ${
              timeRange === "24hours" ? "active" : ""
            }`}
            onClick={() => onTimeRangeChange("24hours")}
            style={{
              padding: "5px 10px",
              backgroundColor: timeRange === "24hours" ? "#4285F4" : "#f0f0f0",
              color: timeRange === "24hours" ? "white" : "black",
              border: "1px solid #ccc",
              borderRadius: "4px",
              cursor: "pointer",
            }}
          >
            24 Hours
          </button>
        </div>
      )}

      <GaugeChart
        id="gauge-chart5"
        nrOfLevels={420}
        arcsLength={[0.3, 0.5, 0.2]}
        colors={["#f60707", "#f9e424", "#09b11d"]}
        percent={getGaugeValue()}
        arcPadding={0.02}
        textColor="#000"
        needleColor="red"
        formatTextValue={getGaugeFormatter()}
      />

      <div
        className="small-graph"
        style={{ width: "100%" }}
        onClick={() => setShowLargeGraph(true)}
      >
        <LineChart
          height={150}
          series={[
            {
              data: uData,
              label:
                dataType === "tankLevel"
                  ? "Diesel Level"
                  : dataType === "tankTemperature"
                  ? "Temperature"
                  : "Battery",
              area: true,
              showMark: true,
              color: "#2a8ef9",
              valueFormatter: (value, context) => {
                const dateIndex = context?.dataIndex;
                if (dateIndex !== undefined && actualDates[dateIndex]) {
                  const dateStr =
                    timeRange === "24hours"
                      ? formatDateTimeForTooltip(actualDates[dateIndex])
                      : formatDateForTooltip(actualDates[dateIndex]);
                  return `${value}${
                    dataType === "tankTemperature" ? "°C" : "%"
                  }\n-(${dateStr})`;
                }
                return `${value}${dataType === "tankTemperature" ? "°C" : "%"}`;
              },
              curve: "natural",
            },
          ]}
          xAxis={[
            {
              scaleType: "point",
              data: chartLabels,
              tickSize: 0,
              disableTicks: true,
              hideTooltip: true,
              label: "",
            },
          ]}
          yAxis={[
            {
              disableTicks: true,
              tickSize: 0,
              hideTooltip: true,
              label: "",
            },
          ]}
          disableAxisListener={true}
          margin={margin}
        />
      </div>

      {props.showLargeGraph && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>{getChartTitle()}</h3>

            <div className="large-graph">
              <LineChart
                height={300}
                width={600}
                series={[
                  {
                    data: uData,
                    label:
                      dataType === "tankLevel"
                        ? "Diesel Level"
                        : dataType === "tankTemperature"
                        ? "Temperature"
                        : "Battery",
                    area: true,
                    showMark: true,
                    color: "#2a8ef9",
                    valueFormatter: (value, context) => {
                      const dateIndex = context?.dataIndex;
                      if (dateIndex !== undefined && actualDates[dateIndex]) {
                        const dateStr =
                          timeRange === "24hours"
                            ? formatDateTimeForTooltip(actualDates[dateIndex])
                            : formatDateForTooltip(actualDates[dateIndex]);
                        return `${value}${
                          dataType === "tankTemperature" ? "°C" : "%"
                        }\n-(${dateStr})`;
                      }
                      return `${value}${
                        dataType === "tankTemperature" ? "°C" : "%"
                      }`;
                    },
                    curve: "natural" as const,
                  },
                ]}
                xAxis={[
                  {
                    scaleType: "point" as const,
                    data: chartLabels,
                    label: timeRange === "24hours" ? "Hour" : "Day",
                  },
                ]}
                yAxis={[
                  {
                    id: "mainAxis",
                    scaleType: "linear" as const,
                    min: getYAxisDomain()[0],
                    max: getYAxisDomain()[1],
                    label:
                      dataType === "tankLevel"
                        ? "Level (%)"
                        : dataType === "tankTemperature"
                        ? "Temperature (°C)"
                        : "Level (%)",
                  },
                ]}
                sx={{
                  [`& .${lineElementClasses.root}`]: {
                    display: "none",
                  },
                  "& .MuiLineElement-series-Volume, & .MuiLineElement-series-Temperature, & .MuiLineElement-series-Battery":
                    {
                      stroke: "#4285F4",
                      strokeWidth: 2,
                    },
                }}
                margin={largeMargin}
              />
            </div>

            {props.closeLargeGraph && (
              <button
                onClick={props.closeLargeGraph}
                className="modal-close-button"
                aria-label="Close modal"
              >
                ×
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default Chart;
